<script setup lang="ts">
import {ref, watchEffect} from "vue";
import {watchDebounced} from "@vueuse/core";
import {shareQuestion, shareMultipleQuestions} from "@/Services/questions";
import {getContacts as getContactsFromApi} from "@/Services/contacts";
import type {AutoCompleteCompleteEvent} from "primevue/autocomplete";
import Str from "@/Utils/Str";
import {AutoComplete, Avatar, Button, Card, Dialog, Label} from "@/Components/Library";
import {useMainStore} from "@/Stores/store";
import {useConfirm} from "primevue/useconfirm";

const $store = useMainStore();
const confirm = useConfirm();

const {question} = defineProps<{
    question?: {
        id: string;
        title: string;
    } | null;
}>();

const model = defineModel("visible", {type: Boolean, default: false});
const emit = defineEmits<{
    (e: "sharingStarted"): void;
}>();

const searchQuery = ref<string>("");
const contacts = ref([]);
const selectedContact = ref<{
    id: string;
    first_name: string;
    last_name: string;
    name: string;
    profile?: {
        color: {
            class: string;
        };
    };
} | null>(null);
const loading = ref(false);

const handleContactSelect = (event) => {
    const fullContact = contacts.value.find((c) => c.id === event.value?.id);
    if (fullContact) {
        selectedContact.value = fullContact;
    }
};

const share = async () => {
    if (!selectedContact.value || !question) {
        return;
    }

    try {
        loading.value = true;
        await shareQuestion(question?.id, {user_id: selectedContact.value.id});
        $store.toastMessages.push({
            severity: "success",
            summary: "Question shared",
            detail: `The question has been shared with ${selectedContact.value.name}.`,
            life: 3000,
        });
        selectedContact.value = null;
        model.value = false;
    } catch (error) {
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const shareMultiple = async () => {
    if (!selectedContact.value || !$store.selectedQuestions) {
        return;
    }

    confirm.require({
        header: "Share posts",
        message: `Are you sure you want to share ${$store.selectedQuestions.length} post(s)?`,
        acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
        accept: async () => {
            try {
                loading.value = true;
                await shareMultipleQuestions($store.selectedQuestions, selectedContact.value.id);
                $store.toastMessages.push({
                    severity: "success",
                    summary: "Sharing Started",
                    detail: `${$store.selectedQuestions.length} post(s) are being shared with ${selectedContact.value.name}. You will be notified when complete.`,
                    life: 3000,
                });
                selectedContact.value = null;
                model.value = false;
                emit("sharingStarted");
            } catch (error) {
                console.error(error);
            } finally {
                loading.value = false;
            }
        },
    });
};

const getContacts = async () => {
    try {
        const {data} = await getContactsFromApi({
            filter: {
                search: searchQuery.value,
            },
            include: ["profile"],
        });
        contacts.value = data;
    } catch (error) {
        console.error(error);
    }
};

watchEffect(() => {
    if (model.value) {
        getContacts();
    }
});

watchDebounced(searchQuery, getContacts, {debounce: 1000});
</script>
<template>
    <Dialog
        v-model:visible="model"
        modal
        dismissable-mask
        class="max-w-md"
        :header="
            !!$store.selectedQuestions.length
                ? `Share ${$store.selectedQuestions.length} Post${$store.selectedQuestions.length === 1 ? '' : 's'}`
                : `Share Post ${Str.of(question?.title ?? '').truncate(20).value}`
        "
    >
        <form @submit.prevent="!!$store.selectedQuestions.length ? shareMultiple() : share()">
            <div class="mb-6 pt-2" v-auto-animate>
                <template v-if="selectedContact">
                    <Card class="rounded-md border dark:border-stone-700">
                        <div class="flex">
                            <h3 class="mb-2 font-bold">Selected Contact</h3>
                            <Button
                                @click="selectedContact = null"
                                icon="mdi:times"
                                class="ml-auto h-8 w-8 bg-red-600 text-white"
                            />
                        </div>
                        <div class="flex items-center gap-2">
                            <Avatar
                                :user="selectedContact"
                                class="flex h-10 w-10 shrink-0"
                                :class="[`bg-${selectedContact.profile?.color.class ?? 'stone-300'}`]"
                                shape="circle"
                            />
                            <div class="flex grow flex-col">
                                <div>
                                    <span class="font-medium">{{ selectedContact.first_name }}</span>
                                    <span class="ml-1.5 font-bold">{{ selectedContact.last_name }}</span>
                                </div>
                            </div>
                        </div>
                    </Card>
                </template>
                <template v-else>
                    <div class="flex flex-col">
                        <Label for="contact" class="mb-1" required>Contact</Label>
                        <AutoComplete
                            fluid
                            input-id="contact"
                            :items="contacts"
                            dropdown
                            dropdown-mode="current"
                            @complete="($event: AutoCompleteCompleteEvent) => (searchQuery = $event.query)"
                            @option-select="handleContactSelect"
                        />
                    </div>
                </template>
            </div>
            <Button
                type="submit"
                class="w-full bg-askr-green text-white hover:brightness-110"
                label="Share"
                :disabled="!selectedContact"
                :loading="loading"
            />
        </form>
    </Dialog>
</template>
<style scoped></style>
